import { skipToken } from '@apollo/client/react'
import { compact, uniq } from 'lodash-es'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineChevronLeft from '~icons/ic/baseline-chevron-left'
import IcBaselineChevronRight from '~icons/ic/baseline-chevron-right'
import IcOutlineCancel from '~icons/ic/outline-cancel'
import IcOutlineCheckCircle from '~icons/ic/outline-check-circle'
import IcOutlineCloudUpload from '~icons/ic/outline-cloud-upload'
import IcOutlineInfo from '~icons/ic/outline-info'
import { openSignModal } from 'src/components/Account/utils'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { UITooltipTriggerWrapper } from 'src/components/ui/Tooltip'
import { useInfinityListMyTasks } from 'src/services/apollo/task'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { getNodesFromConnection } from 'src/utils/graphql'
import { useHoverMediaQuery } from 'src/utils/hooks/useHoverMediaQuery'
import { getImageThumbnailUrl } from 'src/utils/media'
import { getMediaIdsFromTask } from 'src/utils/task'
import { track } from 'src/utils/tracker'
import { UITooltipHelpIcon } from '../HelpIcon'
import {
  MediaSelectPreviewAddBtn,
  MediaSelectPreviewSelectedImagesArea,
} from '../Media/MediaSelectPreviewPanel'
import {
  useImageItemListCount,
  useImageItemListIsNotEmpty,
  useImageItemListValue,
  useSetImageItemList,
} from '../Media/MediaSelectPreviewPanel/context'
import { ImageItemsSourceContext, TrainLoraDatasetContext } from './context'
import { MAX_ALLOWED_TRAIN_LORA_MEDIA } from './utils'
import {
  ClearImagesBtn,
  useSyncDatasetFromTrainingTaskQs,
  TrainLoraImportHistoryButton,
  TrainLoraImportDatasetButton,
} from './TrainLoraImageImport'

const MIN_REQUIRED_IMAGES = 10

function getProgressColor(count: number): string {
  if (count < MIN_REQUIRED_IMAGES) return 'bg-red-500'
  if (count < 30) return 'bg-green-500'
  if (count < 50) return 'bg-teal-500'
  if (count < 70) return 'bg-blue-500'
  return 'bg-violet-500'
}

function DatasetProgressBar() {
  const { t } = useTranslation(['market', 'trainlora'])
  const count = useImageItemListCount()
  const imageItems = useImageItemListValue()
  const readyCount = imageItems.filter(id => !id.startsWith('upload')).length
  const prevReadyCountRef = useRef(readyCount)
  const percentage = Math.min(
    (readyCount / MAX_ALLOWED_TRAIN_LORA_MEDIA) * 100,
    100,
  )
  const color = getProgressColor(readyCount)
  const isReady = readyCount >= MIN_REQUIRED_IMAGES

  useEffect(() => {
    if (
      prevReadyCountRef.current < MIN_REQUIRED_IMAGES &&
      readyCount >= MIN_REQUIRED_IMAGES
    ) {
      toast.success(t('trainlora:submit-form.notice.min-images-reached'))
    }
    prevReadyCountRef.current = readyCount
  }, [readyCount, t])

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1.5">
        <h2 className="font-medium">
          {t('market:train-lora.image-select.image-count')}
          <span
            className={cx(
              'ml-1 font-semibold',
              isReady ? 'text-green-600 dark:text-green-400' : 'text-red-500',
            )}
          >
            {readyCount}
            {readyCount !== count && (
              <span className="text-neutral-400">({count})</span>
            )}
            /{MAX_ALLOWED_TRAIN_LORA_MEDIA}
          </span>
        </h2>
        <ExampleImagesHelpIcon />
      </div>
      <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
        <div
          className={cx(
            'h-full rounded-full transition-all duration-300 ease-out',
            color,
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-xs text-neutral-400">
        <span
          className={cx(
            !isReady && 'text-red-500 dark:text-red-400 font-medium',
          )}
        >
          {t('trainlora:submit-form.image-select.min-required', {
            count: MIN_REQUIRED_IMAGES,
          })}
        </span>
        <span>
          {t('trainlora:submit-form.image-select.max-count', {
            count: MAX_ALLOWED_TRAIN_LORA_MEDIA,
          })}
        </span>
      </div>
    </div>
  )
}

const RECENT_TASKS_PAGE_SIZE = 8

type RecentTask = {
  id: string
  coverMediaId: string
  allMediaIds: string[]
}

function RecentArtworks() {
  const { me } = useMe()
  const { t } = useTranslation(['trainlora'])
  const { data, loading, loadingMore, fetchMore } = useInfinityListMyTasks(
    me ? { last: RECENT_TASKS_PAGE_SIZE } : skipToken,
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(
    new Set(),
  )

  const recentTasks = useMemo<RecentTask[]>(() => {
    const tasks = getNodesFromConnection(data)?.reverse() ?? []
    const seen = new Set<string>()
    return compact(
      tasks.map(task => {
        const allMediaIds = getMediaIdsFromTask(task)
        const coverMediaId = allMediaIds[0] as string | undefined
        if (!coverMediaId || seen.has(task.id)) return null
        seen.add(task.id)
        return { id: task.id, coverMediaId, allMediaIds }
      }),
    )
  }, [data])

  const hasMore = data?.pageInfo?.hasPreviousPage ?? false

  const onScrollEnd = useCallback(() => {
    if (loadingMore || !hasMore) return
    const el = scrollRef.current
    if (!el) return
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 50) {
      fetchMore({
        variables: {
          before: data?.pageInfo.startCursor,
          last: RECENT_TASKS_PAGE_SIZE,
        },
      })
    }
  }, [loadingMore, hasMore, data?.pageInfo.startCursor, fetchMore])

  const toggleMedia = (mediaId: string) => {
    setSelectedMediaIds(prev => {
      const next = new Set(prev)
      if (next.has(mediaId)) {
        next.delete(mediaId)
      } else {
        next.add(mediaId)
      }
      return next
    })
  }

  const imageItemsSourceRef = useContext(ImageItemsSourceContext)
  const setImageItemList = useSetImageItemList()

  const onImportSelected = () => {
    const ids = Array.from(selectedMediaIds)
    track('dataset_image_import_from_recent', { count: ids.length })
    setImageItemList(prev => uniq([...prev, ...ids]))
    ids.forEach(id => {
      imageItemsSourceRef.current?.set(id, 'task')
    })
    setSelectedMediaIds(new Set())
  }

  if (!me || (loading && recentTasks.length === 0)) return null
  if (recentTasks.length === 0) return null

  return (
    <div className="w-full min-w-0 bg-neutral-50/80 dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-100 dark:border-neutral-700 overflow-hidden">
      <div className="flex justify-between items-center mb-3">
        <span className="text-base font-semibold text-neutral-700 dark:text-neutral-200">
          {t('trainlora:submit-form.image-select.recent-tasks')}
        </span>
        <div className="flex items-center gap-2">
          {selectedMediaIds.size > 0 && (
            <button
              type="button"
              className="text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 px-3 py-1 rounded-full transition-colors"
              onClick={() => onImportSelected()}
            >
              {t('trainlora:submit-form.image-select.import-selected', {
                count: selectedMediaIds.size,
              })}
            </button>
          )}
          <TrainLoraImportHistoryButton variant="link" />
        </div>
      </div>
      <RecentTasksScrollArea
        scrollRef={scrollRef}
        onScrollEnd={onScrollEnd}
        recentTasks={recentTasks}
        selectedMediaIds={selectedMediaIds}
        toggleMedia={toggleMedia}
        loadingMore={loadingMore}
      />
    </div>
  )
}

const TASK_CARD_WIDTH = 168 // 10.5rem
const TASK_CARD_GAP = 12 // gap-3
const SCROLL_STEP = 2 * (TASK_CARD_WIDTH + TASK_CARD_GAP)

function RecentTasksScrollArea(props: {
  scrollRef: React.RefObject<HTMLDivElement | null>
  onScrollEnd: () => void
  recentTasks: RecentTask[]
  selectedMediaIds: Set<string>
  toggleMedia: (id: string) => void
  loadingMore: boolean
}) {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateArrows = useCallback(() => {
    const el = props.scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [props.scrollRef])

  useEffect(() => {
    updateArrows()
  }, [props.recentTasks.length, updateArrows])

  const onScroll = useCallback(() => {
    updateArrows()
    props.onScrollEnd()
  }, [updateArrows, props.onScrollEnd])

  const scroll = (direction: 'left' | 'right') => {
    const el = props.scrollRef.current
    if (!el) return
    el.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP,
      behavior: 'smooth',
    })
  }

  return (
    <div className="relative">
      <div
        ref={props.scrollRef}
        onScroll={onScroll}
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollbarWidth: 'thin' }}
      >
        {props.recentTasks.map(task => {
          const isSelected = task.allMediaIds.some(id =>
            props.selectedMediaIds.has(id),
          )
          const selectedCount = task.allMediaIds.filter(id =>
            props.selectedMediaIds.has(id),
          ).length
          const hasMultiple = task.allMediaIds.length > 1

          const cardClassName = cx(
            'shrink-0 w-[10.5rem] h-[10.5rem] rounded-lg bg-neutral-200 dark:bg-neutral-700 relative cursor-pointer border-2 transition-all',
            isSelected
              ? 'border-violet-500 ring-2 ring-violet-200 dark:ring-violet-800'
              : 'border-transparent hover:border-violet-300',
          )

          const cardContent = (
            <>
              <img
                src={getImageThumbnailUrl(task.coverMediaId)}
                alt=""
                loading="lazy"
                className="w-full h-full object-contain rounded-md"
              />
              {selectedCount > 0 && (
                <div className="absolute top-1 right-1 bg-violet-600 rounded-full px-1.5 py-0.5 text-[10px] text-white font-bold">
                  {selectedCount}
                </div>
              )}
              {hasMultiple && (
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                  {task.allMediaIds.length}
                </div>
              )}
            </>
          )

          if (hasMultiple) {
            return (
              <RecentTaskMultiCard
                key={task.id}
                task={task}
                cardClassName={cardClassName}
                cardContent={cardContent}
                selectedMediaIds={props.selectedMediaIds}
                toggleMedia={props.toggleMedia}
              />
            )
          }

          return (
            <button
              type="button"
              key={task.id}
              className={cardClassName}
              onClick={() => {
                props.toggleMedia(task.coverMediaId)
              }}
            >
              {cardContent}
            </button>
          )
        })}
        {props.loadingMore && (
          <div className="shrink-0 w-[10.5rem] h-[10.5rem] rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            <div className="size-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      {canScrollLeft && (
        <button
          type="button"
          className="absolute left-0 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 dark:bg-white/20 dark:hover:bg-white/30 text-white rounded-full p-1 transition-colors"
          onClick={e => {
            e.stopPropagation()
            track('dataset_scroll_arrow_click', { direction: 'left' })
            scroll('left')
          }}
        >
          <IcBaselineChevronLeft className="size-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          className="absolute right-0 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 dark:bg-white/20 dark:hover:bg-white/30 text-white rounded-full p-1 transition-colors"
          onClick={e => {
            e.stopPropagation()
            track('dataset_scroll_arrow_click', { direction: 'right' })
            scroll('right')
          }}
        >
          <IcBaselineChevronRight className="size-5" />
        </button>
      )}
    </div>
  )
}

function MobileTaskSelectionDialog(props: {
  task: RecentTask
  initialSelected: Set<string>
  toggleMedia: (id: string) => void
  onClose: () => void
}) {
  const [localSelected, setLocalSelected] = useState(
    () =>
      new Set(
        props.task.allMediaIds.filter(id => props.initialSelected.has(id)),
      ),
  )

  const toggleLocal = (mediaId: string) => {
    setLocalSelected(prev => {
      const next = new Set(prev)
      if (next.has(mediaId)) {
        next.delete(mediaId)
      } else {
        next.add(mediaId)
      }
      return next
    })
  }

  const onConfirm = () => {
    props.task.allMediaIds.forEach(mediaId => {
      const wasSelected = props.initialSelected.has(mediaId)
      const isNowSelected = localSelected.has(mediaId)
      if (wasSelected !== isNowSelected) {
        props.toggleMedia(mediaId)
      }
    })
    props.onClose()
  }

  return (
    <>
      <UIDialogContent>
        <div className="grid grid-cols-2 gap-3 max-h-[264px] overflow-y-auto">
          {props.task.allMediaIds.map(mediaId => {
            const isMediaSelected = localSelected.has(mediaId)
            return (
              <button
                type="button"
                key={mediaId}
                className={cx(
                  'w-[120px] h-[120px] rounded-lg overflow-hidden relative cursor-pointer border-2 transition-all shrink-0',
                  isMediaSelected
                    ? 'border-violet-500 ring-2 ring-violet-200 dark:ring-violet-800'
                    : 'border-transparent hover:border-violet-300',
                )}
                onClick={() => toggleLocal(mediaId)}
              >
                <img
                  src={getImageThumbnailUrl(mediaId)}
                  alt=""
                  className="w-full h-full object-contain"
                />
                {isMediaSelected && (
                  <div className="absolute top-1 right-1 bg-violet-600 rounded-full p-0.5">
                    <IcOutlineCheckCircle className="size-4 text-white" />
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton
          variant="filled"
          className="max-msm:flex-1 min-w-16 capitalize"
          onPress={onConfirm}
        >
          <Text i18nKey="action:ok" />
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

function RecentTaskMultiCard(props: {
  task: RecentTask
  cardClassName: string
  cardContent: React.ReactNode
  selectedMediaIds: Set<string>
  toggleMedia: (id: string) => void
}) {
  const canHover = useHoverMediaQuery()
  const { task, cardClassName, cardContent, selectedMediaIds, toggleMedia } =
    props

  const mediaGrid = (
    <div className="grid grid-cols-2 gap-3 w-[264px] p-2 max-h-[268px] overflow-y-auto">
      {task.allMediaIds.map(mediaId => {
        const isMediaSelected = selectedMediaIds.has(mediaId)
        return (
          <button
            type="button"
            key={mediaId}
            className={cx(
              'w-[120px] h-[120px] rounded-lg overflow-hidden relative cursor-pointer border-2 transition-all shrink-0',
              isMediaSelected
                ? 'border-violet-500 ring-2 ring-violet-200 dark:ring-violet-800'
                : 'border-transparent hover:border-violet-300',
            )}
            onClick={() => toggleMedia(mediaId)}
          >
            <img
              src={getImageThumbnailUrl(mediaId)}
              alt=""
              className="w-full h-full object-contain"
            />
            {isMediaSelected && (
              <div className="absolute top-1 right-1 bg-violet-600 rounded-full p-0.5">
                <IcOutlineCheckCircle className="size-4 text-white" />
              </div>
            )}
          </button>
        )
      })}
    </div>
  )

  if (canHover) {
    return (
      <UITooltipTriggerWrapper
        delay={250}
        trigger={
          <AriaButton className={cardClassName}>{cardContent}</AriaButton>
        }
        placement="top"
        className="flex"
      >
        {mediaGrid}
      </UITooltipTriggerWrapper>
    )
  }

  return (
    <AriaButton
      className={cardClassName}
      onPress={() => {
        UIDialog.create({
          responsive: false,
          children: ({ close }) => (
            <MobileTaskSelectionDialog
              task={task}
              initialSelected={selectedMediaIds}
              toggleMedia={toggleMedia}
              onClose={close}
            />
          ),
        })
      }}
    >
      {cardContent}
    </AriaButton>
  )
}

function DatasetQuickImportSection(props: { onUploadClick: () => void }) {
  const { me } = useMe()
  const { t } = useTranslation(['market', 'trainlora'])
  return (
    <div className="flex flex-col items-center gap-4 px-4 pb-20 pt-8">
      <div className="w-full h-px bg-neutral-200 dark:bg-neutral-700" />
      <RecentArtworks />
      {/* Divider */}
      <div className="w-full flex items-center gap-4">
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
        <span className="text-xs text-neutral-400 font-medium uppercase tracking-wider">
          {t('trainlora:submit-form.image-select.or-drop-images')}
        </span>
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
      </div>
      <div className="flex gap-4 w-full">
        <button
          type="button"
          className="flex-1 flex justify-center items-center gap-1.5 rounded-lg font-medium px-4 py-2.5 text-sm bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 transition-colors shadow-sm"
          onClick={e => {
            e.stopPropagation()
            track('dataset_upload_empty_state_click')
            if (!me?.id) {
              openSignModal()
            } else {
              props.onUploadClick()
            }
          }}
        >
          <IcOutlineCloudUpload className="size-4" />
          {t('market:train-lora.add-image.label')}
        </button>
        <TrainLoraImportDatasetButton variant="secondary" />
      </div>
    </div>
  )
}

function UploadTrigger(props: { open: () => void; disabled?: boolean }) {
  const { me } = useMe()
  const { t } = useTranslation(['market', 'trainlora'])
  return (
    <div className="flex flex-col items-center gap-5 px-6 py-6 w-full">
      {/* Hero icon + title */}
      <div className="flex flex-col items-center gap-2">
        <TrainLoraImportHistoryButton variant="icon" />
        <h3 className="text-base font-bold text-neutral-800 dark:text-neutral-100">
          {t('trainlora:submit-form.image-select.select-from-history')}
        </h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-sm">
          {t('trainlora:submit-form.image-select.select-from-history-desc')}
        </p>
      </div>

      {/* Recent tasks quick select */}
      <RecentArtworks />

      {/* Divider */}
      <div className="w-full flex items-center gap-4">
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
        <span className="text-xs text-neutral-400 font-medium uppercase tracking-wider">
          {t('trainlora:submit-form.image-select.or-drop-images')}
        </span>
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
      </div>

      {/* Secondary actions */}
      <div className="flex gap-4 w-full">
        <button
          type="button"
          disabled={props.disabled}
          className="flex-1 flex justify-center items-center gap-1.5 rounded-lg font-medium px-4 py-2.5 text-sm bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={e => {
            e.stopPropagation()
            track('dataset_upload_empty_state_click')
            if (!me?.id) {
              openSignModal()
            } else {
              props.open()
            }
          }}
        >
          <IcOutlineCloudUpload className="size-4" />
          {t('market:train-lora.add-image.label')}
        </button>
        <TrainLoraImportDatasetButton variant="secondary" />
      </div>
    </div>
  )
}

export default function TrainLoraDatasetDisplay() {
  const { t } = useTranslation(['market'])
  const hasAddedImage = useImageItemListIsNotEmpty()
  const { me } = useMe()
  const imageItemsSourceRef = useContext(ImageItemsSourceContext)
  const [{ datasetMode }, setDatasetCtx] = useContext(TrainLoraDatasetContext)
  const isRetrainMode = datasetMode !== 'default'
  const openFileDialogRef = useRef<(() => void) | undefined>(undefined)
  const onDropRef = useRef<((files: File[]) => void) | undefined>(undefined)
  useSyncDatasetFromTrainingTaskQs()

  const handleContainerDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault()
    }
  }, [])

  const handleContainerDrop = useCallback(
    (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return
      e.preventDefault()
      if (!me?.id || !hasAddedImage || isRetrainMode) return
      const acceptedTypes = new Set(['image/png', 'image/jpeg', 'image/webp'])
      const files = Array.from(e.dataTransfer.files).filter(f =>
        acceptedTypes.has(f.type),
      )
      if (files.length > 0) {
        onDropRef.current?.(files)
      }
    },
    [me?.id, hasAddedImage, isRetrainMode],
  )

  return (
    <section className="flex flex-col lg:h-full min-w-0">
      <DatasetProgressBar />
      <div
        className={cx(
          'rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 flex flex-col transition-colors',
          !hasAddedImage &&
            'bg-white dark:bg-neutral-800 hover:border-violet-300 dark:hover:border-violet-600 hover:bg-violet-50/30 dark:hover:bg-violet-950/10',
          hasAddedImage && 'bg-neutral-100 dark:bg-neutral-800',
          hasAddedImage ? 'grow overflow-hidden' : 'xl:grow',
        )}
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
      >
        {hasAddedImage && (
          <div className="shrink-0 flex items-center justify-end gap-2 px-4 pt-4">
            {isRetrainMode && (
              <button
                type="button"
                className="rounded-full border border-neutral-300 dark:border-neutral-600 px-3 py-1 text-xs font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                onClick={() => {
                  track('exit_reuse_dataset_mode_click')
                  setDatasetCtx({
                    datasetMode: 'default',
                    datasetId: undefined,
                  })
                }}
              >
                {t('market:train-lora.image-select.switch-to-default-mode')}
              </button>
            )}
            <ClearImagesBtn />
          </div>
        )}
        <div
          className={cx(
            'gap-3',
            hasAddedImage
              ? 'p-4 grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] overflow-y-auto'
              : 'w-full h-full flex flex-col',
          )}
        >
          <MediaSelectPreviewAddBtn
            onFileDialogOpen={() => {
              track('dataset_image_upload_click')
            }}
            onItemUploadSuccess={mediaId => {
              imageItemsSourceRef.current?.set(mediaId, 'upload')
            }}
            onUploadSuccess={() => {
              track('dataset_image_upload_success')
            }}
            maxAllowedMedia={MAX_ALLOWED_TRAIN_LORA_MEDIA}
            wrapperProps={{
              className: cx(
                'rounded-lg',
                hasAddedImage
                  ? 'hidden'
                  : 'w-full h-full grow !cursor-default [&>svg]:hidden',
                isRetrainMode && '!cursor-not-allowed opacity-50',
              ),
            }}
            dropzoneOpts={{
              disabled: !me?.id || isRetrainMode,
              noClick: !hasAddedImage,
            }}
            openRef={openFileDialogRef}
            onDropRef={onDropRef}
            uploadTrigger={({ open }) => (
              <UploadTrigger open={open} disabled={isRetrainMode} />
            )}
          />
          <MediaSelectPreviewSelectedImagesArea
            onRemove={itemId => {
              imageItemsSourceRef.current?.delete(itemId)
            }}
            wrapperProps={{
              className:
                'h-full w-full rounded-lg bg-white dark:bg-neutral-700',
            }}
            imgProps={{
              className:
                'aspect-square object-contain h-full w-full rounded-lg',
            }}
            disableRemove={isRetrainMode}
          />
          {hasAddedImage && !isRetrainMode && (
            <div className="col-[1/-1]">
              <DatasetQuickImportSection
                onUploadClick={() => openFileDialogRef.current?.()}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function ExampleImagesHelpIcon() {
  const { t } = useTranslation(['market'])
  return (
    <div className="flex items-center gap-1 text-sm">
      <span>{t('market:train-lora.image-select.example-images')}</span>
      <UITooltipHelpIcon
        iconComp={IcOutlineInfo}
        variant="normal"
        TooltipProps={{
          placement: 'bottom',
        }}
      >
        <article className="flex gap-4">
          {(
            [
              [
                t('market:train-lora.example.clear-subject.label'),
                'https://images-ng.pixai.art/images/thumb/92a17a91-9c4f-465b-a481-8847806d654d',
                <IcOutlineCheckCircle
                  key="icon"
                  className="text-emerald-500"
                />,
              ],
              [
                t('market:train-lora.example.distinctive-style.label'),
                'https://images-ng.pixai.art/images/thumb/f1991be8-e2ab-4ea0-a744-b6a0228c8e8f',
                <IcOutlineCheckCircle
                  key="icon"
                  className="text-emerald-500"
                />,
              ],
              [
                t('market:train-lora.example.blur-image.label'),
                'https://images-ng.pixai.art/images/thumb/1313334b-8c64-464b-aa6f-6443dc4a428a',
                <IcOutlineCancel key="icon" className="text-rose-400" />,
              ],
            ] as Array<[string, string, JSX.Element]>
          ).map(([label, url, icon], idx) => (
            <figure key={idx}>
              <img
                width={120}
                height={120}
                className="object-cover rounded-[6px]"
                src={url}
                alt=""
              />
              <figcaption className="mt-1 flex justify-between text-sm items-center">
                {label} {icon}
              </figcaption>
            </figure>
          ))}
        </article>
      </UITooltipHelpIcon>
    </div>
  )
}
